import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button, Col, Container, FormGroup, Label, Modal, ModalBody, Row, Input } from 'reactstrap';
import useApi from 'vl-common/src/hooks/useApi';
import { AlertMessage } from 'vl-common/src/components/AlertMessage/AlertMessage';
import { LoadingButton } from 'vl-common/src/components/LoadingButton';
import { getMetaAppointmentsQuery } from 'vl-common/src/schemas/getMetaAppointments';
import { successModal } from 'vl-common/src/components/Modals';
import useFetchSpecialityAndSubSpeciality from 'vl-common/src/hooks/useFetchSpecialityAndSubSpeciality';
import { Appointment } from 'vl-common/src/schemas/shared';
import { useRuntimeConfig } from 'vl-common/src/hooks/Runtime';

interface AddF2FReferralProps {
  open: boolean;
  appointment: Appointment;
  patient_user_guid: string;
  onCancel: () => void;
  onCreated: () => void;
}

interface FormState {
  appt_type: string;
  clin_type: string;
  clin_spec: string;
  clin_area: string;
  audit_reason: string;
}

export const AddF2FReferralModal = ({
  open,
  appointment,
  onCancel,
  onCreated,
  patient_user_guid
}: AddF2FReferralProps) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const { CLIENTADMIN_REFERRAL_SPECS } = useRuntimeConfig();

  const { appointment_id, spec_cat_code, case_spec_area_code } = appointment;
  const [formState, setFormState] = useState<FormState>({
    appt_type: 'NEW',
    audit_reason: '',
    clin_area: '',
    clin_spec: '',
    clin_type: 'DOCTOR'
  });

  const [showAlertMessage, setShowAlertMessage] = useState({
    visibility: false,
    msg: ''
  });
  const [specialities, subSpecialities] = useFetchSpecialityAndSubSpeciality();

  const displayAlertMessage = (msg = '') => {
    setShowAlertMessage({ visibility: true, msg });
  };
  const hideAlertMessage = () => {
    setShowAlertMessage({ visibility: false, msg: '' });
  };

  const { data: appointmentsMeta } = useQuery({
    ...getMetaAppointmentsQuery({ appt_id: appointment_id })
  });

  useEffect(() => {
    if (!appointmentsMeta?.referral_reasons) {
      setShowAlertMessage({ msg: 'This appointment type has not been configured for F2F referrals', visibility: true });
    } else {
      setShowAlertMessage({ visibility: false, msg: '' });
    }
  }, [appointmentsMeta?.referral_reasons]);

  const { mutate: addReferral, isPending: isUpdatingAuth } = useMutation({
    mutationKey: ['updateAddF2FReferral', appointment_id],
    mutationFn: async () => {
      return Api.referrer.postReferral({
        appt_id: appointment_id,
        ref_reason_code: formState.audit_reason,
        speccat: formState.clin_spec,
        specarea: formState.clin_area,
        user_guid: patient_user_guid,
        urg_code: 'ROU',
        ca_creating: 1
      });
    },

    onError: (error: any) => {
      const errorMessage: string = error?.response?.data?.message;
      if (errorMessage) {
        displayAlertMessage(errorMessage);
      } else {
        displayAlertMessage('Bad Request, Try again.');
      }
    },

    onSuccess: () => {
      successModal({ title: 'Added F2F Referral successfully' });
      onCreated();
    }
  });

  const hasSubSpecialities = useCallback(
    (spec_cat: string) => subSpecialities.find((area) => area.category_code === spec_cat),
    [subSpecialities]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;

      if (name === 'clin_spec') {
        const specHasSubSpecialities = subSpecialities.some((area) => area.category_code === value);
        setFormState((prevState) => ({
          ...prevState,
          clin_spec: value,
          clin_area: specHasSubSpecialities ? prevState.clin_area : ''
        }));
      } else {
        setFormState((prevState) => ({ ...prevState, [name]: value }));
      }
    },
    [subSpecialities]
  );

  useEffect(() => {
    if (!appointment) return;

    const hasLoadedData = specialities.length > 0 && subSpecialities.length > 0;

    if (hasLoadedData) {
      const specHasSubSpecialities = subSpecialities.some((area) => area.category_code === spec_cat_code);

      setFormState((prevState) => ({
        ...prevState,
        clin_spec: spec_cat_code,
        clin_area: specHasSubSpecialities && case_spec_area_code ? case_spec_area_code : ''
      }));
    }
  }, [appointment, spec_cat_code, case_spec_area_code, specialities.length, subSpecialities.length, subSpecialities]);

  return (
    <Modal
      key={appointment_id}
      size="lg"
      isOpen={open}
      centered
      className="vl-add-modal"
      data-testid="add-F2FReferral-modal"
    >
      <ModalBody className="p-4">
        <Container>
          <Row>
            <Col xs="12">
              <span className="vl-modal-title" data-testid="add-F2FReferral-header">
                {t('Add F2F Referral')}
              </span>
            </Col>
            <AlertMessage message={showAlertMessage.msg} visibility={showAlertMessage.visibility} />
            <Col>
              <br />
              <FormGroup>
                <Label for="apptTypeSelect">{t('Appointment type')}</Label>
                <Input id="apptTypeSelect" name="apptTypeSelect" type="select" readOnly>
                  <option value="">{t('F2F consultation')}</option>
                </Input>
                <br />
                <Label for="clinTypeSelect">{t('Clinician type')}</Label>
                <Input id="clinTypeSelect" name="clinTypeSelect" type="select" readOnly>
                  <option value="">{t('F2F consultant')}</option>
                </Input>
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col xs="6">
                    <Label for="clinSpeciality">
                      {t('Clinician Speciality')}
                      <span className="vl-product-satsuma">*</span>
                    </Label>
                    <Input
                      id="clinSpeciality"
                      name="clin_spec"
                      type="select"
                      disabled={!CLIENTADMIN_REFERRAL_SPECS}
                      onChange={handleChange}
                      value={formState.clin_spec}
                      style={formState.clin_spec ? {} : { color: 'lightgrey' }}
                    >
                      <option disabled selected value="">
                        {t('Select Speciality')}
                      </option>
                      {specialities.map(({ cat_code, cat_name }) => (
                        <option key={cat_code} value={cat_code} style={styles.enabledOption}>
                          {cat_name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col xs="6" hidden={!hasSubSpecialities(formState.clin_spec)}>
                    <Label for="clinSubSpeciality">
                      {t('Clinician Sub-Speciality')}
                      <span className="vl-product-satsuma">*</span>
                    </Label>
                    <Input
                      id="clinSubSpeciality"
                      name="clin_area"
                      type="select"
                      disabled={!CLIENTADMIN_REFERRAL_SPECS}
                      onChange={handleChange}
                      value={formState.clin_area}
                      style={formState.clin_area ? {} : { color: 'lightgrey' }}
                    >
                      {!formState.clin_area && (
                        <option disabled value="">
                          {t('Select Sub-Speciality')}
                        </option>
                      )}
                      {subSpecialities
                        .filter(({ category_code }) => category_code === formState.clin_spec)
                        .map(({ area_name, area_code }) => (
                          <option key={area_code} value={area_code} style={styles.enabledOption}>
                            {area_name}
                          </option>
                        ))}
                    </Input>
                  </Col>
                </Row>
                <br />
                <Label for="clinAuditReason">
                  {t('Referral Reason')}
                  <span className="vl-product-satsuma">*</span>
                </Label>
                <Input
                  id="clinAuditReason"
                  name="audit_reason"
                  type="select"
                  onChange={handleChange}
                  value={formState.audit_reason}
                  style={formState.audit_reason ? {} : { color: 'lightgrey' }}
                >
                  <option disabled value="">
                    {t('Select Reason')}
                  </option>
                  {appointmentsMeta?.referral_reasons?.map(({ ref_reason_code, ref_reason_desc }) => (
                    <option key={ref_reason_code} value={ref_reason_code} style={styles.enabledOption}>
                      {ref_reason_desc}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            {/*<pre>{JSON.stringify(formState, null, 2)}</pre>*/}
            <Col xs="12" className="text-right" style={styles.buttonContainer}>
              <Button
                className="vl-button-gray vl-font-regular"
                type="button"
                disabled={isUpdatingAuth}
                onClick={() => {
                  hideAlertMessage();
                  setFormState({
                    appt_type: 'NEW',
                    audit_reason: '',
                    clin_area: '',
                    clin_spec: '',
                    clin_type: 'DOCTOR'
                  });
                  onCancel();
                }}
                data-testid="cancel-f2f-referal-modal-button"
              >
                {t('Cancel')}
              </Button>
              <LoadingButton
                className="vl-button vl-font-regular"
                disabled={
                  isUpdatingAuth ||
                  !appointment_id ||
                  !formState.audit_reason ||
                  !formState.clin_spec ||
                  (hasSubSpecialities(formState.clin_spec) && !formState.clin_area)
                }
                onClick={() => {
                  hideAlertMessage();
                  addReferral();
                }}
                loading={isUpdatingAuth}
                data-testid="add-f2f-referral-button"
              >
                {t('Add F2F Referral')}
              </LoadingButton>
            </Col>
          </Row>
        </Container>
      </ModalBody>
    </Modal>
  );
};

const styles = {
  info: { marginBottom: '1rem' },
  label: { marginBottom: '1rem', width: '50%' },
  buttonContainer: { display: 'flex', justifyContent: 'flex-end' },
  enabledOption: { color: '#000' }
};

export default AddF2FReferralModal;
